import { PublicLayout } from "@/components/PublicLayout";
import { ShieldCheck } from "lucide-react";

const LAST_UPDATED = "October 21, 2026";

const sections = [
  {
    id: "intro",
    title: "1. Introduction",
    body: [
      "Sparkd (\"we\", \"us\", \"our\") operates the Sparkd dating mobile application and related services (the \"Service\"). This Privacy Policy explains how we collect, use, disclose and safeguard your information when you use the Service.",
      "By creating an account or using Sparkd, you agree to the collection and use of information in accordance with this policy. If you do not agree, please do not use the Service.",
    ],
  },
  {
    id: "data-we-collect",
    title: "2. Information We Collect",
    body: [
      "Account & Profile Data: name, date of birth, gender, email address, phone number, photos, bio, sexual orientation and dating preferences you choose to provide.",
      "Usage Data: how you interact with the app, matches, likes, messages metadata, features used, and time spent.",
      "Device & Technical Data: device identifiers, IP address, operating system, app version and crash logs.",
      "Location Data: precise GPS location only with your permission and only while a live venue check-in or Squad Watchdog session is active (see Geolocation & Radar below).",
      "Payment Data: if you purchase a subscription, payment is processed by our payment provider; we do not store full card numbers.",
    ],
  },
  {
    id: "biometrics",
    title: "Biometric Data & Didit Verification",
    body: [
      "Sparkd does NOT store or own biometric templates or raw facial geometry. Biometric liveness checks and government ID validation are processed by our verification provider, Didit.",
      "Sparkd stores only a cryptographic verification token and a boolean verification flag (verified / not verified) on your account.",
      "Biometric data is never sold, leased, or shared by Sparkd with any third party.",
    ],
  },
  {
    id: "geolocation",
    title: "Geolocation & Radar",
    body: [
      "GPS location is collected strictly when you initiate a live venue check-in (a 200m–400m perimeter around the venue) or arm Squad Watchdog.",
      "Location check-in sessions operate on an automated 2.5-hour time-to-live (TTL) and expire immediately without passive background tracking.",
      "Sparkd never tracks your location passively in the background once you are checked out.",
    ],
  },
  {
    id: "ephemeral",
    title: "Ephemeral Data Purges & 6:00 AM Reset",
    body: [
      "Venue check-in records, squad huddle threads, and expired chat tokens are subject to automated daily hard purges at 6:00 AM local time.",
      "Purged data is permanently destroyed and cannot be recovered by you or by Sparkd.",
    ],
  },
  {
    id: "coop-media",
    title: "Co-Op & Wingman Media Rights",
    body: [
      "When you tag a co-star in a Co-Op or Wingman video, you grant Sparkd a non-exclusive license to display that media across both tagged profiles.",
      "This license lasts until the media is deleted, or the tag is revoked, by either tagged user.",
    ],
  },
  {
    id: "sos-escrow",
    title: "Law Enforcement Escrow (SOS)",
    body: [
      "When an SOS is triggered, Sparkd encrypts a tactical visual snapshot consisting of your GPS location, a recent photo, and a timestamp.",
      "This snapshot is held in escrow and is accessible exclusively by verified public safety personnel presenting a valid CAD (Computer-Aided Dispatch) incident number.",
    ],
  },
  {
    id: "cookies",
    title: "Cookies and Similar Tracking Technologies",
    body: [
      "Sparkd uses cookies, web beacons, SDKs, and local storage mechanisms to authenticate users, protect security, analyze usage trends, and measure campaign effectiveness.",
      "1. Strictly Necessary Technologies: We utilize essential session tokens and local device storage required to maintain secure user authentication, prevent bot activity, facilitate identity verification through Didit, and prevent fraud. These cannot be disabled as the app and member portal cannot function without them.",
      "2. Functional & Preference Technologies: We store user preferences, such as theme settings, display parameters, and localized app states, to provide a consistent user experience.",
      "3. Analytics & Attribution: We use aggregated performance tracking to analyze web traffic on sparkd-app.com and measure attribution for mobile app install campaigns. This data is processed in an anonymized, aggregated format.",
      "4. No Sale or Brokerage: Sparkd does not sell, lease, or trade personal data or tracking identifiers to third-party data brokers.",
      "5. Managing Tracking: Users may configure browser settings to block or delete web cookies. In-app device tracking preferences can be adjusted via iOS Tracking Settings or Android Privacy Settings. Disabling certain essential storage mechanisms may degrade application functionality.",
      "For questions regarding this policy, contact privacy@sparkd-app.com.",
    ],
  },
  {
    id: "how-we-use",
    title: "3. How We Use Your Information",
    body: [
      "To create and manage your account and profile.",
      "To provide our core matching service and show you relevant profiles.",
      "To enable messaging between matched users.",
      "To keep the community safe, detect fraud, and enforce our guidelines.",
      "To send you service-related communications and, where permitted, marketing messages you can opt out of.",
      "To comply with legal obligations.",
    ],
  },
  {
    id: "sharing",
    title: "4. How We Share Information",
    body: [
      "With other users: your profile information and photos are visible to other users as part of the matching service.",
      "With service providers: hosting, analytics, payment and customer-support vendors who process data on our behalf under contract.",
      "For legal reasons: when required by law, subpoena, or to protect the rights and safety of our users.",
      "We do not sell your personal data to third parties.",
    ],
  },
  {
    id: "retention",
    title: "5. Data Retention",
    body: [
      "We keep your personal data for as long as your account is active. When you delete your account, we remove or anonymize your personal data within 30 days, except where we must retain certain information to comply with legal obligations or resolve disputes.",
      "Account Deletion (Apple Guideline 5.1.1(v)): you can delete your account self-serve inside the Sparkd app (Settings → Delete Account) or on the web at https://sparkd-app.com/delete-account.",
      "Deletion requests enter a 30-day compliance grace period, after which your data undergoes immutable cryptographic deletion and cannot be restored.",
    ],
  },
  {
    id: "your-rights",
    title: "6. Your Privacy Rights",
    body: [
      "Depending on your location, you may have the right to access, correct, export or delete your personal data, and to object to or restrict certain processing.",
      "You can request account deletion at any time using our Account Deletion Request page. To exercise other rights, contact us at privacy@sparkd-app.com.",
    ],
  },
  {
    id: "security",
    title: "7. Security",
    body: [
      "We use industry-standard technical and organizational measures to protect your data, including encryption in transit and access controls. No method of transmission over the internet is 100% secure, but we work hard to protect your information.",
    ],
  },
  {
    id: "children",
    title: "8. Children's Privacy",
    body: [
      "Sparkd is strictly for users aged 18 and older. We do not knowingly collect data from anyone under 18. If we learn that we have collected such data, we will delete it promptly.",
    ],
  },
  {
    id: "changes",
    title: "9. Changes to This Policy",
    body: [
      "We may update this Privacy Policy from time to time. We will notify you of material changes through the app or by email, and update the \"Last updated\" date above.",
    ],
  },
  {
    id: "contact",
    title: "10. Contact Us",
    body: [
      "If you have questions about this Privacy Policy or your data, contact our privacy team at privacy@sparkd-app.com or submit a support ticket through this portal.",
    ],
  },
];

export default function Privacy() {
  return (
    <PublicLayout>
      <div className="spark-grid-bg border-b border-[#2A313C]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#2A313C] bg-[#1F242D] text-xs uppercase font-semibold tracking-[0.2em] text-[#E11D48]">
            <ShieldCheck className="w-3.5 h-3.5" /> Legal
          </span>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white" data-testid="privacy-title">
            Privacy &amp; Cookie Policy
          </h1>
          <p className="mt-3 text-slate-400">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-[220px_1fr] gap-10">
        <nav className="hidden lg:block sticky top-24 self-start">
          <p className="text-xs uppercase font-semibold tracking-[0.2em] text-slate-500 mb-3">On this page</p>
          <ul className="space-y-2 text-sm">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-slate-400 hover:text-[#E11D48] transition-colors">
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <article className="space-y-8">
          {sections.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24 rounded-2xl spark-card p-6 sm:p-8">
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-4">{s.title}</h2>
              <div className="space-y-3">
                {s.body.map((p, i) => (
                  <p key={i} className="text-[#CBD5E1] leading-relaxed text-[15px]">{p}</p>
                ))}
              </div>
            </section>
          ))}
        </article>
      </div>
    </PublicLayout>
  );
}
